import { useEffect, useRef, useState } from "react";
import { Microphone, CheckCircle, ArrowClockwise, WarningCircle } from "@phosphor-icons/react";

const SECONDS = 3;
const MIN_PEAK = 0.04;

export function MicCheck({ onPassed }) {
  const [phase, setPhase] = useState("idle"); // idle | recording | review | passed | error
  const [level, setLevel] = useState(0);
  const [url, setUrl] = useState("");
  const [heard, setHeard] = useState(false);
  const [msg, setMsg] = useState("");
  const cleanup = useRef(() => {});

  useEffect(() => () => { cleanup.current(); if (url) URL.revokeObjectURL(url); }, [url]);

  const record = async () => {
    setMsg(""); setHeard(false); setLevel(0);
    if (url) { URL.revokeObjectURL(url); setUrl(""); }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setPhase("error");
      setMsg("We couldn't use your microphone. Click the lock icon next to the web address, allow the microphone, then try again.");
      return;
    }
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    let peak = 0;
    let raf;
    const tick = () => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i] * buf[i];
      const rms = Math.sqrt(sum / buf.length);
      peak = Math.max(peak, rms);
      setLevel(Math.min(1, rms * 6));
      raf = requestAnimationFrame(tick);
    };
    tick();

    const rec = new MediaRecorder(stream);
    const chunks = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    const stopAll = () => { cancelAnimationFrame(raf); stream.getTracks().forEach((t) => t.stop()); ctx.close().catch(() => {}); };
    cleanup.current = stopAll;
    rec.onstop = () => {
      stopAll();
      setLevel(0);
      if (peak < MIN_PEAK) {
        setPhase("error");
        setMsg("We didn't pick up any sound. Check your microphone isn't muted and speak a little louder, then try again.");
        return;
      }
      setUrl(URL.createObjectURL(new Blob(chunks, { type: rec.mimeType || "audio/webm" })));
      setPhase("review");
    };
    rec.start();
    setPhase("recording");
    setTimeout(() => rec.state !== "inactive" && rec.stop(), SECONDS * 1000);
  };

  const confirm = () => { setPhase("passed"); onPassed(); };

  if (phase === "passed") {
    return (
      <div className="mt-8 flex items-center gap-3 border-2 border-emerald-600 bg-emerald-600/10 p-5 text-sm font-bold" data-testid="mic-check-passed">
        <CheckCircle size={22} weight="fill" className="text-emerald-600" /> Microphone works — you're ready to start.
      </div>
    );
  }

  return (
    <div className="mt-8 border-2 border-signal bg-signal/10 p-5" data-testid="mic-check">
      <div className="flex gap-4">
        <Microphone size={26} weight="fill" className="text-signal shrink-0" />
        <div className="text-sm leading-relaxed flex-1 min-w-0">
          <div className="font-bold text-foreground">Check your microphone first</div>
          <p className="mt-1 text-foreground/80">
            The last section (Speaking) records your voice. Click the button, allow the microphone when your browser asks,
            and say <span className="font-bold">"Hello, my name is …"</span> for {SECONDS} seconds. You must pass this check to start.
          </p>

          <div className="mt-4 h-2 bg-foreground/10 overflow-hidden" aria-label="Sound level" data-testid="mic-check-meter">
            <div className="h-full bg-signal transition-[width] duration-75" style={{ width: `${Math.round(level * 100)}%` }} />
          </div>

          {phase === "review" && (
            <div className="mt-4 space-y-3" data-testid="mic-check-review">
              <audio controls src={url || undefined} className="w-full" data-testid="mic-check-playback" />
              <label className="flex items-center gap-2 font-bold cursor-pointer">
                <input type="checkbox" className="accent-[#ff8000] w-4 h-4" checked={heard} onChange={(e) => setHeard(e.target.checked)} data-testid="mic-check-heard-checkbox" />
                I can hear my voice clearly
              </label>
            </div>
          )}

          {msg && (
            <p className="mt-3 flex gap-2 text-red-600 font-bold" data-testid="mic-check-error">
              <WarningCircle size={18} weight="fill" className="shrink-0" /> {msg}
            </p>
          )}

          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={record}
              disabled={phase === "recording"}
              data-testid="mic-check-record-btn"
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold border border-foreground hover:bg-foreground hover:text-background disabled:opacity-60"
            >
              {phase === "recording" ? <>Recording… speak now</> : phase === "idle" ? <><Microphone size={15} weight="bold" /> Record a {SECONDS}-second test</> : <><ArrowClockwise size={15} weight="bold" /> Record again</>}
            </button>
            {phase === "review" && (
              <button
                type="button"
                onClick={confirm}
                disabled={!heard}
                data-testid="mic-check-confirm-btn"
                className="px-4 py-2 text-sm font-bold bg-foreground text-background border border-foreground disabled:opacity-40"
              >
                My microphone works
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
